import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import FloatingModal from './ModalVariants';

/* ── Expandable wrapper (shared by Textarea and HTML fields) ── */

type ExpandableFieldProps = {
  children: React.ReactNode;
  /** Render the full-size editor inside the expand modal */
  renderExpanded: () => React.ReactNode;
  expandable?: boolean;
  expandLabel?: string;
};

/**
 * Generic expand-button + modal wrapper.
 * Wraps any inline editor and adds an "Expand" affordance in the top-right
 * that opens a FloatingModal with a larger editing surface.
 *
 * While the modal is open, the inline slot renders a non-editable placeholder
 * instead of the live editor. On collapse the inline editor remounts and
 * mount-seeds from the current draft (via RichHtmlField's useLayoutEffect).
 *
 * CONTRACT WARNING: children with internal/uncontrolled state are UNMOUNTED
 * while expanded — their state is destroyed. Children must be controlled
 * (value-driven) so the post-collapse remount re-seeds from current state;
 * an uncontrolled child (e.g. textarea with defaultValue) would lose user
 * input on expand. All current production usages are controlled or disabled.
 */
export function ExpandableField({
  children,
  renderExpanded,
  expandable = true,
  expandLabel = 'Expand editor',
}: ExpandableFieldProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isFocused, setIsFocused] = useState(false);

  const collapse = () => {
    setIsExpanded(false);
  };

  return (
    <div
      className="relative group"
      onFocusCapture={() => setIsFocused(true)}
      onBlurCapture={() => setIsFocused(false)}
    >
      {isExpanded ? (
        /* While modal is open, freeze the inline slot as a non-editable
           placeholder. On collapse, children remount and mount-seed from the
           current draft via RichHtmlField's useLayoutEffect. */
        <div aria-hidden className="min-h-[40px] rounded border border-dashed border-border-secondary bg-bg-secondary" />
      ) : children}
      {expandable && (
        <button
          type="button"
          aria-label={expandLabel}
          title={expandLabel}
          onClick={() => setIsExpanded(true)}
          className={`absolute right-2 top-2 text-icon-subtle hover:text-content-tertiary bg-bg-raised/80 hover:bg-bg-raised shadow-sm rounded-md px-2 py-1 text-xs transition-opacity ${
            isFocused ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'
          }`}
        >
          Expand
        </button>
      )}
      {isExpanded && (
        <FloatingModal
          open
          title="Edit"
          onClose={collapse}
          onRequestClose={() => true}
          className="w-[min(100%,64rem)]"
          hideCloseButton={false}
        >
          <div className="w-full">{renderExpanded()}</div>
          <div className="mt-4 flex justify-end">
            <button
              type="button"
              className="px-3 py-2 rounded-md border border-border-primary bg-bg-raised hover:bg-bg-secondary text-content-tertiary"
              onClick={collapse}
            >
              Done
            </button>
          </div>
        </FloatingModal>
      )}
    </div>
  );
}

/* ── Textarea ── */

type TextareaProps = React.TextareaHTMLAttributes<HTMLTextAreaElement> & {
  // Auto-grow to fit content up to a max height cap.
  autoExpand?: boolean;
  // Max auto height in pixels (fallback if viewport not available).
  maxAutoHeightPx?: number;
  // Show an explicit Expand affordance that opens a large editor overlay.
  expandable?: boolean;
  // Label for the expand button (a11y)
  expandLabel?: string;
  // Height of the expanded editor in viewport height
  expandEditorVh?: number;
};

export function Textarea(props: TextareaProps) {
  const {
    className = '',
    autoExpand = true,
    maxAutoHeightPx,
    expandable = true,
    expandLabel = 'Expand editor',
    expandEditorVh = 70,
    onChange,
    onInput,
    rows = 3,
    ...rest
  } = props;

  const textareaRef = useRef<HTMLTextAreaElement | null>(null);

  // Compute a sensible cap: min(40vh, 480px) unless overridden.
  const capPx = useMemo(() => {
    if (typeof maxAutoHeightPx === 'number' && maxAutoHeightPx > 0) return maxAutoHeightPx;
    try {
      const viewport = typeof window !== 'undefined' ? window.innerHeight : 0;
      if (viewport > 0) return Math.min(Math.round(viewport * 0.4), 480);
    } catch {}
    return 480;
  }, [maxAutoHeightPx]);

  const resizeToContent = useCallback(() => {
    if (!autoExpand) return;
    const el = textareaRef.current;
    if (!el) return;
    // Preserve current scroll context to avoid jerk
    const container = el.closest('[data-modal-body-scroll]') as HTMLElement | null;
    const prevScrollTop = container?.scrollTop ?? 0;
    const prevSelectionStart = el.selectionStart;
    const prevSelectionEnd = el.selectionEnd;

    el.style.height = 'auto';
    const next = Math.min(el.scrollHeight, capPx);
    el.style.height = `${next}px`;
    // Show inner scrollbar only if we exceed the cap
    el.style.overflowY = el.scrollHeight > capPx ? 'auto' : 'hidden';
    // Restore scroll/caret where possible
    if (container) container.scrollTop = prevScrollTop;
    try {
      if (typeof prevSelectionStart === 'number' && typeof prevSelectionEnd === 'number') {
        el.setSelectionRange(prevSelectionStart, prevSelectionEnd);
      }
    } catch {}
  }, [autoExpand, capPx]);

  // Resize on mount and whenever value changes.
  // resizeToContent is a useCallback([autoExpand, capPx]); capPx is already in this
  // dep array and autoExpand doesn't change at runtime, so including resizeToContent
  // directly would cause unnecessary re-runs on every render.
  useLayoutEffect(() => {
    resizeToContent();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- see comment above
  }, [rest.value, rest.defaultValue, capPx]);

  const handleInput = useCallback(
    (e: React.FormEvent<HTMLTextAreaElement>) => {
      // Delegate to caller handlers first
      onInput?.(e);
      onChange?.(e as any);
      // Then resize
      resizeToContent();
    },
    [onChange, onInput, resizeToContent]
  );

  // Mark modal body scroll container for better scroll anchoring restoration.
  useEffect(() => {
    // The modal variants use a scrollable body; add a data attribute at runtime if missing.
    const findScrollableAncestors = () => {
      const el = textareaRef.current;
      if (!el) return;
      let p: HTMLElement | null = el.parentElement;
      while (p) {
        const style = window.getComputedStyle(p);
        if (style.overflowY === 'auto' || style.overflowY === 'scroll') {
          p.setAttribute('data-modal-body-scroll', 'true');
          break;
        }
        p = p.parentElement;
      }
    };
    try { findScrollableAncestors(); } catch {}
  }, []);

  return (
    <ExpandableField
      expandable={expandable}
      expandLabel={expandLabel}
      renderExpanded={() => (
        <textarea
          className="w-full rounded-md border border-border-primary bg-bg-secondary text-content-primary p-3 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
          style={{ height: `${expandEditorVh}vh` }}
          value={(rest.value as any) ?? ('' as any)}
          onChange={(e) => {
            onChange?.(e as any);
            setTimeout(resizeToContent, 0);
          }}
        />
      )}
    >
      <textarea
        ref={textareaRef}
        rows={rows}
        className={`mt-1 block w-full rounded-md border border-border-primary bg-bg-secondary text-content-primary p-2 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-[height] duration-150 ease-out resize-y ${className}`}
        onInput={handleInput}
        {...rest}
      />
    </ExpandableField>
  );
}

